import {
  DispatchCatalogFreightLineDraft,
  DispatchFreightContext,
  DispatchFreightDraft,
  DispatchFreightLineDraft,
  DispatchFreightMetricsDraft,
  DispatchFreightValidationIssue,
  DispatchFreightValidationOptions,
  DispatchManualFreightLineDraft,
  DispatchPreferredRateMethod,
  DispatchRateLocation,
  DispatchRatingFreight,
  DispatchRatingItem,
  DispatchRatingRequest,
} from '../models/dispatch-freight.models';

export class DispatchFreightValidationError extends Error {
  constructor(readonly issues: DispatchFreightValidationIssue[]) {
    super('Dispatch freight is not ready for rating.');
    this.name = 'DispatchFreightValidationError';
  }
}

export function createEmptyDispatchFreightDraft(): DispatchFreightDraft {
  return {
    freight: {
      total_weight_lb: null,
      linear_feet: null,
      handling_units: null,
      cubic_feet: null,
      density_lb_per_cuft: null,
      delivery_type: null,
      preferred_rate_method: null,
      load_type: '',
    },
    items: [],
  };
}

export function cloneDispatchFreightDraft(draft: DispatchFreightDraft): DispatchFreightDraft {
  return {
    freight: { ...draft.freight },
    items: draft.items.map((item) => ({ ...item })),
  };
}

export function validateDispatchFreightDraft(
  draft: DispatchFreightDraft,
  context: DispatchFreightContext,
  options: DispatchFreightValidationOptions = {},
): DispatchFreightValidationIssue[] {
  const issues: DispatchFreightValidationIssue[] = [];

  if (options.requireCustomer && !cleanString(context.customer_contact_uuid)) {
    issues.push({ path: 'customer_contact_uuid', message: 'Select a customer.' });
  }

  validateLocation(context.origin, 'origin', 'Origin', issues);
  validateLocation(context.destination, 'destination', 'Destination', issues);
  validateMetrics(draft.freight, issues);

  if (draft.items.length === 0) {
    issues.push({ path: 'freight_items', message: 'Add at least one freight line.' });
  }

  draft.items.forEach((item, index) => validateLine(item, index, issues));
  return issues;
}

export function buildDispatchRatingRequest(
  draft: DispatchFreightDraft,
  context: DispatchFreightContext,
  options: DispatchFreightValidationOptions = {},
): DispatchRatingRequest {
  const issues = validateDispatchFreightDraft(draft, context, options);
  if (issues.length > 0) {
    throw new DispatchFreightValidationError(issues);
  }

  const payload: DispatchRatingRequest = {
    origin: normalizeLocation(context.origin),
    destination: normalizeLocation(context.destination),
    freight: buildFreight(draft.freight),
  };

  const freightItems = draft.items.map(buildRatingItem);
  if (freightItems.length > 0) {
    payload.freight_items = freightItems;
  }

  const customerContactUuid = cleanString(context.customer_contact_uuid);
  if (customerContactUuid) {
    payload.customer_contact_uuid = customerContactUuid;
  }

  return payload;
}

function validateLocation(
  location: DispatchRateLocation,
  path: 'origin' | 'destination',
  label: string,
  issues: DispatchFreightValidationIssue[],
): void {
  const hasPostal = cleanString(location.postal_code).length > 0;
  const hasCityRegion =
    cleanString(location.city).length > 0 && cleanString(location.province).length > 0;

  if (!hasPostal && !hasCityRegion) {
    issues.push({ path, message: `${label} requires a postal code or city and region.` });
  }
}

function validateMetrics(
  freight: DispatchFreightMetricsDraft,
  issues: DispatchFreightValidationIssue[],
): void {
  const positiveFields: Array<keyof Pick<
    DispatchFreightMetricsDraft,
    'total_weight_lb' | 'linear_feet' | 'handling_units' | 'cubic_feet' | 'density_lb_per_cuft'
  >> = [
    'total_weight_lb',
    'linear_feet',
    'handling_units',
    'cubic_feet',
    'density_lb_per_cuft',
  ];

  positiveFields.forEach((field) => {
    const value = freight[field];
    if (value !== null && !isPositiveFinite(value)) {
      issues.push({ path: `freight.${field}`, message: `${field} must be greater than zero.` });
    }
  });

  if (
    freight.delivery_type !== null &&
    (!Number.isFinite(freight.delivery_type) || freight.delivery_type < 0)
  ) {
    issues.push({ path: 'freight.delivery_type', message: 'delivery_type cannot be negative.' });
  }

  if (
    freight.preferred_rate_method !== null &&
    !isPreferredRateMethod(freight.preferred_rate_method)
  ) {
    issues.push({
      path: 'freight.preferred_rate_method',
      message: 'preferred_rate_method must be between 1 and 4.',
    });
  }
}

function validateLine(
  item: DispatchFreightLineDraft,
  index: number,
  issues: DispatchFreightValidationIssue[],
): void {
  const path = `freight_items.${index}`;
  if (!isPositiveWholeNumber(item.package_quantity)) {
    issues.push({ path: `${path}.package_quantity`, message: 'Quantity must be a positive whole number.' });
  }

  if (item.source === 'catalog') {
    if (!cleanString(item.customer_product_uuid)) {
      issues.push({ path: `${path}.customer_product_uuid`, message: 'Select a customer product.' });
    }
    return;
  }

  (['length', 'width', 'height'] as const).forEach((field) => {
    if (!isPositiveFinite(item[field])) {
      issues.push({ path: `${path}.${field}`, message: `${field} must be greater than zero.` });
    }
  });

  if (item.weight !== null && !isPositiveFinite(item.weight)) {
    issues.push({ path: `${path}.weight`, message: 'Weight must be greater than zero when provided.' });
  }
}

function buildFreight(draft: DispatchFreightMetricsDraft): DispatchRatingFreight {
  const freight: DispatchRatingFreight = {};
  assignPositiveNumber(freight, 'total_weight_lb', draft.total_weight_lb);
  assignPositiveNumber(freight, 'linear_feet', draft.linear_feet);
  assignPositiveNumber(freight, 'handling_units', draft.handling_units);
  assignPositiveNumber(freight, 'cubic_feet', draft.cubic_feet);
  assignPositiveNumber(freight, 'density_lb_per_cuft', draft.density_lb_per_cuft);

  if (draft.delivery_type !== null) {
    freight.delivery_type = draft.delivery_type;
  }
  if (draft.preferred_rate_method !== null) {
    freight.preferred_rate_method = draft.preferred_rate_method;
  }

  const loadType = cleanString(draft.load_type);
  if (loadType) {
    freight.load_type = loadType;
  }
  return freight;
}

function buildRatingItem(item: DispatchFreightLineDraft): DispatchRatingItem {
  if (item.source === 'catalog') {
    return buildCatalogItem(item);
  }
  return buildManualItem(item);
}

function buildCatalogItem(item: DispatchCatalogFreightLineDraft): DispatchRatingItem {
  return {
    source: 'catalog',
    customer_product_uuid: cleanString(item.customer_product_uuid),
    package_quantity: item.package_quantity as number,
    floor_rotation_allowed: item.floor_rotation_allowed,
  };
}

function buildManualItem(item: DispatchManualFreightLineDraft): DispatchRatingItem {
  const normalized = {
    source: 'manual' as const,
    ...(cleanString(item.label) ? { label: cleanString(item.label) } : {}),
    package_quantity: item.package_quantity as number,
    length: item.length as number,
    width: item.width as number,
    height: item.height as number,
    ...(item.weight !== null ? { weight: item.weight } : {}),
    measurement_unit: item.measurement_unit,
    weight_unit: item.weight_unit,
    stackable: item.stackable,
    floor_rotation_allowed: item.floor_rotation_allowed,
  };
  return normalized;
}

function normalizeLocation(location: DispatchRateLocation): DispatchRateLocation {
  const normalized: DispatchRateLocation = {};
  const postalCode = cleanString(location.postal_code).toUpperCase();
  const city = cleanString(location.city);
  const province = cleanString(location.province).toUpperCase();
  const country = normalizeCountry(cleanString(location.country));
  const contactUuid = cleanString(location.contact_uuid);
  const contactAddressUuid = cleanString(location.contact_address_uuid);

  if (postalCode) normalized.postal_code = postalCode;
  if (city) normalized.city = city;
  if (province) normalized.province = province;
  if (country) normalized.country = country;
  if (contactUuid) normalized.contact_uuid = contactUuid;
  if (contactAddressUuid) normalized.contact_address_uuid = contactAddressUuid;
  return normalized;
}

function normalizeCountry(value: string): string {
  const upper = value.toUpperCase();
  if (upper === 'CANADA' || upper === 'CAN') return 'CA';
  if (upper === 'UNITED STATES' || upper === 'UNITED STATES OF AMERICA' || upper === 'USA') return 'US';
  return upper;
}

function assignPositiveNumber<K extends keyof DispatchRatingFreight>(
  freight: DispatchRatingFreight,
  key: K,
  value: number | null,
): void {
  if (value !== null && isPositiveFinite(value)) {
    freight[key] = value as DispatchRatingFreight[K];
  }
}

function cleanString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function isPositiveFinite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

function isPositiveWholeNumber(value: unknown): value is number {
  return isPositiveFinite(value) && Number.isInteger(value);
}

function isPreferredRateMethod(value: number): value is DispatchPreferredRateMethod {
  return value === 1 || value === 2 || value === 3 || value === 4;
}
